/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import type { TrafficFilterRulesetInfo } from '@modules/cloud-api/v1/types'
import { makeBlankRuleset } from '@modules/network-security-lib/makeBlankRuleset'
import {
  useCreateTrafficFilterRulesetMutation,
  useUpdateTrafficFilterRulesetMutation,
  useCreateTrafficFilterRulesetAssociationMutation,
  useDeleteTrafficFilterRulesetAssociationMutation,
} from '@modules/cloud-lib/networkSecurity/hooks'
import type { RulesetType } from '@modules/network-security-lib/types'
import { makeAssociationsDiff } from '@modules/network-security-lib/makeAssociationsDiff'
import { addToast } from '@modules/cui/Toasts'
import { makeTrafficFilterRulesetRequestFromInfo } from '@modules/network-security-lib/makeTrafficFilterRulesetRequestFromInfo'
import { wrapAsAsyncRequestError } from '@modules/query/wrapAsAsyncRequestError'

import IpFilterForm from './IpFilterForm'
import PrivateConnectionForm from './PrivateConnectionForm'

type Props = {
  onClose: () => void
} & (
  | {
      // Supply to edit an existing ruleset
      ruleset: TrafficFilterRulesetInfo | null
      newRulesetType?: never
    }
  | {
      // Supply to create a new ruleset
      newRulesetType: RulesetType
      ruleset?: never
    }
)

const TrafficFilterForm: React.FunctionComponent<Props> = ({
  onClose,
  ruleset: rulesetUnderEdit,
  newRulesetType,
}) => {
  const intl = useIntl()

  const isNewRuleset = !rulesetUnderEdit
  const rulesetType =
    newRulesetType || (rulesetUnderEdit?.type === 'ip' ? 'ipFilter' : 'privateConnection')

  const [ruleset, setRuleset] = useState<TrafficFilterRulesetInfo>(
    isNewRuleset ? makeBlankRuleset(rulesetType) : rulesetUnderEdit,
  )

  const setRulesetField = useCallback(function <
    TKey extends Exclude<keyof TrafficFilterRulesetInfo, 'id'>,
    TValue extends TrafficFilterRulesetInfo[TKey],
  >(key: TKey, value: TValue) {
    setRuleset((currentRuleset: TrafficFilterRulesetInfo) => ({
      ...currentRuleset,
      [key]: value,
    }))
  }, [])

  const {
    mutate: createTrafficFilterRuleset,
    isLoading: isCreatingRuleset,
    error: createError,
  } = useCreateTrafficFilterRulesetMutation()
  const {
    mutate: updateTrafficFilterRuleset,
    isLoading: isUpdatingRuleset,
    error: updateError,
  } = useUpdateTrafficFilterRulesetMutation()
  const { mutateAsync: createTrafficFilterRulesetAssociation } =
    useCreateTrafficFilterRulesetAssociationMutation()
  const { mutateAsync: deleteTrafficFilterRulesetAssociation } =
    useDeleteTrafficFilterRulesetAssociationMutation()

  const associationPromises = useRef<Array<Promise<unknown>>>([])

  const isSaving = isCreatingRuleset || isUpdatingRuleset || associationPromises.current.length > 0

  const error = wrapAsAsyncRequestError(createError || updateError)

  const save = () => {
    // Extract the request object from the response object we use for editing (mostly due to ease of dealing with associations)
    const rulesetPayload = makeTrafficFilterRulesetRequestFromInfo(ruleset)

    if (isNewRuleset) {
      createTrafficFilterRuleset({ ruleset: rulesetPayload }, { onSuccess: saveAssociations })
    } else {
      updateTrafficFilterRuleset(
        { rulesetId: ruleset.id, ruleset: rulesetPayload },
        { onSuccess: saveAssociations },
      )
    }

    // The API only handles a single add or remove at a time, so we need to batch these after the main save.
    function saveAssociations({ id: rulesetId }: { id: string }) {
      const { add, remove } = makeAssociationsDiff(
        rulesetUnderEdit?.associations,
        ruleset.associations,
      )

      add.forEach((association) => {
        associationPromises.current.push(
          createTrafficFilterRulesetAssociation({ rulesetId, association }),
        )
      })

      remove.forEach((association) => {
        associationPromises.current.push(
          deleteTrafficFilterRulesetAssociation({ rulesetId, association }),
        )
      })

      // Warn but move on if some associations fail to save.
      Promise.allSettled(associationPromises.current).then((results) => {
        const hasRejected = results.some((result) => result.status === 'rejected')

        if (hasRejected) {
          addToast({
            title: intl.formatMessage({
              id: 'trafficFilter.associations.error.title',
              defaultMessage: 'Warning',
            }),
            color: 'warning',
            iconType: 'warning',
            text: intl.formatMessage({
              id: 'trafficFilter.associations.error.description',
              defaultMessage: 'Some deployment associations may not have been saved successfully.',
            }),
          })
        }

        onClose()
      })
    }
  }

  return rulesetType === 'ipFilter' ? (
    <IpFilterForm
      onClose={onClose}
      ruleset={ruleset}
      setRulesetField={setRulesetField}
      isNewRuleset={isNewRuleset}
      onSave={save}
      isSaving={isSaving}
      error={error}
    />
  ) : (
    <PrivateConnectionForm
      onClose={onClose}
      ruleset={ruleset}
      setRulesetField={setRulesetField}
      isNewRuleset={isNewRuleset}
      onSave={save}
      isSaving={isSaving}
      error={error}
    />
  )
}

export default TrafficFilterForm
